import React from 'react';

import { ChangedAtTimeByUser } from '../../components/ChangedAtTimeByUser';
import { TableActions } from '../../components/TableActions/TableActions';
import type { UserLabelFragment } from '../../graphql/hasura/generated';
import { Refetch, RefetchContext } from '../../utils';
import { formatTimestamp } from '../../utils/formatters';
import {
  HistoryOfChangesModal,
  IHistoryOfChangesModalProps,
} from '../DataChangeLog/components/HistoryOfChangesModal';
import { IModelAction, IModelColumn } from '../typings';

export const createdAtColumn: IModelColumn<{ createdAt: string }> = {
  title: 'Created',
  dataIndex: 'createdAt',
  sorter: true,
  enabled: () => true,
  render: ({ createdAt }) => formatTimestamp(createdAt),
  filterOptions: {
    type: 'DATE_RANGE',
    disableFutureDates: true,
  },
};

export const createdAtAndByColumn: IModelColumn<{
  createdAt: string;
  createdByUser?: UserLabelFragment | null;
}> = {
  title: 'Created',
  enabled: () => true,
  render: ({ createdAt, createdByUser }) => (
    <ChangedAtTimeByUser changedAt={createdAt} changedByUser={createdByUser} />
  ),
};

export const updatedAtColumn: IModelColumn<{ updatedAt: string }> = {
  title: 'Updated',
  dataIndex: 'updatedAt',
  sorter: true,
  enabled: () => true,
  render: ({ updatedAt }) => formatTimestamp(updatedAt),
  filterOptions: {
    type: 'DATE_RANGE',
    disableFutureDates: true,
  },
};

interface IUpdatedAtAndByData {
  updatedAt: string;
  updatedByUser?: UserLabelFragment | null;
}

export const updatedAtAndByColumn: IModelColumn<IUpdatedAtAndByData> = {
  title: 'Updated',
  enabled: () => true,
  render: ({ updatedAt, updatedByUser }) => (
    <ChangedAtTimeByUser changedAt={updatedAt} changedByUser={updatedByUser} />
  ),
};

interface ICreateActionsColumnArgs<TRow> {
  actions: IModelAction<TRow>[];
  refetch?: Refetch;
}

export function createActionsColumn<TRow = any>(args: ICreateActionsColumnArgs<TRow>) {
  const { actions, refetch } = args;

  return {
    title: 'Actions',
    key: 'actions',
    render: (_: any, row: any) => {
      if (typeof refetch === 'function') {
        return (
          <RefetchContext.Provider value={refetch}>
            <TableActions actions={actions} row={row} />
          </RefetchContext.Provider>
        );
      }

      return <TableActions actions={actions} row={row} />;
    },
  };
}

export function createHistoryOfChangesColumn(props: IHistoryOfChangesModalProps) {
  const { tabTables } = props;

  if (!tabTables.some(t => t.tableConfig.enabled(t.tableArgs))) {
    return updatedAtAndByColumn;
  }

  const column: IModelColumn<IUpdatedAtAndByData> = {
    title: 'Updated',
    enabled: () => true,
    render: ({ updatedAt, updatedByUser }) => {
      return (
        // Use line height to center left text with button
        <div style={{ lineHeight: '32px' }}>
          <ChangedAtTimeByUser changedAt={updatedAt} changedByUser={updatedByUser} />
          <div>
            <HistoryOfChangesModal  {...props} />
          </div>
        </div>
      );
    },
  };

  return column;
};
